import { QueryKey, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { SearchResponse } from "@/types/search/tour-search-results";
import { tourSearchResponseFactory } from "@/factories/search/tour-search-response-factory";
import { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import { getCurrencyIdFromLocale } from "@/config/unified-search-config";
import buildQueryString from "@/utils/search-utils";

interface TourSearchProps extends Partial<SearchParameters> {
  currentPage: number;
  pageSize: number;
}

const fetchSearchResults = async ({ queryKey }: { queryKey: QueryKey }): Promise<SearchResponse> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/naming-convention
  const [_, queryParams, currencyId] = queryKey as [unknown, TourSearchProps, string];
  // Extract pagination params and convert the rest to SearchParameters
  const { currentPage, pageSize, ...searchParams } = queryParams;

  // Add default system parameters with locale-based currency ID
  const searchParameters: Partial<SearchParameters> = {
    MarketId: "1",
    CurrencyId: currencyId || "20", // Use passed currency ID or default to USD
    RowCount: 12,
    ...searchParams // User filters override defaults
  };

  // Build query string
  const queryString = buildQueryString(searchParameters);
  const finalQueryString = queryString
    ? `${queryString}&currentPage=${currentPage}`
    : `currentPage=${currentPage}`;

  const apiUrl = `/api/search?${finalQueryString}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Search service error: ${response.status}`);
    }
    const data = await response.json();

    return tourSearchResponseFactory(data);
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? `Search failed: ${error.message}`
        : "Search service is currently unavailable"
    );
  }
};

const useTourSearch = (queryParams: TourSearchProps) => {
  const locale = useLocale();
  const currencyId = getCurrencyIdFromLocale(locale);

  return useQuery({
    queryKey: ["Tour_Search", queryParams, currencyId],
    queryFn: fetchSearchResults,
    staleTime: Infinity
  });
};

export default useTourSearch;